import { useEffect, useState } from "react";
import { PauseCircle, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const inputCls =
  "h-11 w-full rounded-xl border border-slate-200 bg-paper px-4 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal";

const DEFAULT_MESSAGE = "We apologize, rentals are paused for Maintenance and upkeep. Please feel free to browse and sign up for now.";

export const RentalStatusCard = () => {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ enabled: false, pause_from: "", pause_until: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/rental-status").then((r) => {
      setState(r.data);
      setForm({
        enabled: r.data.enabled,
        pause_from: r.data.from || "",
        pause_until: r.data.until || "",
        message: r.data.saved_message || "",
      });
    }).catch(() => {});
  }, []);

  const save = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/rental-status", {
        enabled,
        pause_from: form.pause_from,
        pause_until: form.pause_until,
        message: form.message,
      });
      setState(data);
      setForm((f) => ({ ...f, enabled }));
      if (data.paused) toast.success("Rentals paused — visitors can browse but not book or pay");
      else if (data.scheduled) toast.success(`Pause scheduled from ${data.from}`);
      else toast.success("Rentals are live — booking and payments are on");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="rental-status-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-ink">
            <PauseCircle className="h-5 w-5 text-royal" /> Rentals pause switch
          </h3>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
            visitors can still browse, sign in and sign up — booking &amp; payment turn off
          </p>
        </div>
        <span
          data-testid="rental-status-badge"
          className={`rounded-full px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] ${
            state.paused ? "bg-amber-100 text-amber-800"
            : state.scheduled ? "bg-royal-light text-royal"
            : "bg-emerald-50 text-emerald-600"
          }`}
        >
          {state.paused ? "Paused — booking off" : state.scheduled ? `Pause starts ${state.from}` : "Live — booking on"}
        </span>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">From (optional)</label>
          <input type="date" value={form.pause_from} onChange={(e) => setForm({ ...form, pause_from: e.target.value })}
                 data-testid="rental-pause-from" className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">Until (optional)</label>
          <input type="date" value={form.pause_until} onChange={(e) => setForm({ ...form, pause_until: e.target.value })}
                 data-testid="rental-pause-until" className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">Message shown on the site</label>
          <input value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                 placeholder={DEFAULT_MESSAGE} data-testid="rental-pause-message" className={inputCls} />
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {state.enabled && (
          <button onClick={() => save(false)} disabled={busy}
                  data-testid="rental-resume-button"
                  className="flex items-center gap-2 rounded-full bg-emerald-600 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-emerald-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : state.paused ? "Resume rentals" : "Cancel the pause"}
          </button>
        )}
        <button onClick={() => save(true)} disabled={busy}
                data-testid="rental-pause-button"
                className="flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal disabled:opacity-50">
          <PauseCircle className="h-4 w-4" /> {busy ? "Saving…" : form.pause_from || form.pause_until ? "Save pause window" : state.enabled ? "Keep rentals paused" : "Pause rentals now"}
        </button>
      </div>
      <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
        Leave the dates empty for an immediate open-ended pause. With dates, the pause switches on and off automatically inside the window. Admin manual orders always stay available.
      </p>
    </div>
  );
};
